import React from 'react';
import { SnackbarProvider, SnackbarKey, useSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const notistackRef = React.createRef<SnackbarProvider>();

  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  return (
    <SnackbarProvider
      ref={notistackRef}
      maxSnack={3}
      autoHideDuration={5000}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      preventDuplicate
      dense
      action={(key) => (
        <IconButton
          onClick={onClickDismiss(key)}
          size="small"
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      )}
    >
      {children}
    </SnackbarProvider>
  );
};

// Helper functions for consistent toast usage across the app
export const useToast = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showSuccess = (message: string) => {
    enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  const showError = (error: unknown) => {
    const message = error instanceof Error ? error.message : 'An error occurred';
    enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 5000,
    });
  };

  const showWarning = (message: string) => {
    enqueueSnackbar(message, {
      variant: 'warning',
      autoHideDuration: 4000,
    });
  };

  const showInfo = (message: string) => {
    enqueueSnackbar(message, {
      variant: 'info',
      autoHideDuration: 3000,
    });
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeSnackbar,
  };
}; 